/**
 * Client-side LRU Image Cache
 * 
 * Caches images locally using IndexedDB for offline access.
 * Uses created_at based eviction strategy (oldest articles first).
 * 
 * This module is isolated for easy modification of eviction strategy.
 */

const DB_NAME = 'reedle-image-cache';
// Export for cleanup utilities (pwaUtils.js clearIndexedDB fallback)
export const IMAGE_CACHE_DB_NAME = DB_NAME;
const DB_VERSION = 1;
const STORE_NAME = 'images';
const MAX_CACHE_SIZE_MB = 200; // 200MB limit
const MAX_CACHE_SIZE_BYTES = MAX_CACHE_SIZE_MB * 1024 * 1024;

let db = null;

/**
 * Initialize IndexedDB
 */
async function initDB() {
  if (db) return db;

  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => {
      db = request.result;
      resolve(db);
    };

    request.onupgradeneeded = (event) => {
      const database = event.target.result;
      if (!database.objectStoreNames.contains(STORE_NAME)) {
        const store = database.createObjectStore(STORE_NAME, { keyPath: 'url' });
        store.createIndex('articleCreatedAt', 'articleCreatedAt', { unique: false });
        store.createIndex('cachedAt', 'cachedAt', { unique: false });
      }
    };
  });
}

/**
 * Get cached image blob
 * @param {string} url - Original image URL
 * @returns {Promise<Blob|null>}
 */
export async function getCachedImage(url) {
  try {
    const database = await initDB();
    return new Promise((resolve) => {
      const tx = database.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const request = store.get(url);
      request.onsuccess = () => resolve(request.result?.blob || null);
      request.onerror = () => resolve(null);
    });
  } catch {
    return null;
  }
}

/**
 * Cache an image
 * @param {string} url - Original image URL
 * @param {Blob} blob - Image blob
 * @param {string} articleCreatedAt - Article's created_at for eviction sorting
 */
export async function cacheImage(url, blob, articleCreatedAt) {
  try {
    const database = await initDB();
    return new Promise((resolve, reject) => {
      const tx = database.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      store.put({
        url,
        blob,
        articleCreatedAt: articleCreatedAt || new Date().toISOString(),
        cachedAt: new Date().toISOString(),
        size: blob.size,
      });
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch (e) {
    console.error('Failed to cache image:', e);
  }
}

/**
 * Get total cache size
 * @returns {Promise<number>} Size in bytes
 */
export async function getCacheSize() {
  try {
    const database = await initDB();
    return new Promise((resolve) => {
      const tx = database.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const request = store.getAll();
      request.onsuccess = () => {
        const total = request.result.reduce((sum, item) => sum + (item.size || 0), 0);
        resolve(total);
      };
      request.onerror = () => resolve(0);
    });
  } catch {
    return 0;
  }
}

/**
 * Evict oldest images until under size limit
 * Uses articleCreatedAt for eviction (oldest articles first)
 */
export async function evictOldest() {
  try {
    const database = await initDB();
    const currentSize = await getCacheSize();

    if (currentSize <= MAX_CACHE_SIZE_BYTES) return;

    // Get all entries sorted by articleCreatedAt (oldest first)
    const entries = await new Promise((resolve) => {
      const tx = database.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const index = store.index('articleCreatedAt');
      const request = index.getAll();
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => resolve([]);
    });

    // Delete oldest until under limit
    let freedSize = 0;
    const targetFree = currentSize - MAX_CACHE_SIZE_BYTES + (10 * 1024 * 1024); // Free 10MB extra

    const tx = database.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);

    for (const entry of entries) {
      if (freedSize >= targetFree) break;
      store.delete(entry.url);
      freedSize += entry.size || 0;
    }

    await new Promise((resolve) => {
      tx.oncomplete = resolve;
      tx.onerror = resolve;
    });

    console.log(`Evicted ${freedSize / 1024 / 1024}MB from image cache`);
  } catch (e) {
    console.error('Failed to evict cache:', e);
  }
}

/**
 * Clear all cached images
 */
export async function clearCache() {
  try {
    const database = await initDB();
    return new Promise((resolve) => {
      const tx = database.transaction(STORE_NAME, 'readwrite');
      tx.objectStore(STORE_NAME).clear();
      tx.oncomplete = resolve;
      tx.onerror = resolve;
    });
  } catch {
    // Ignore errors
  }
}

